import { oc } from "@orpc/contract";
import { z } from "zod";
import { IntegrationAvailabilitySchema, IntegrationKey } from "./base";
import {
  FamousIntegrationSchema,
  FamousIntegrationSettingsSchemaWithoutBase,
} from "./famous";
import {
  PlaidIntegrationSchema,
  PlaidIntegrationSettingsSchemaWithoutBase,
  plaidContracts,
} from "./plaid.contract";
import {
  PaymentsIntegrationSchema,
  PaymentsIntegrationSettingsSchemaWithoutBase,
  paymentsIntegrationContracts,
} from "./payments.contract";
import {
  OracleFusionIntegrationSchema,
  OracleFusionIntegrationSettingsSchemaWithoutBase,
  oracleFusionContracts,
} from "./oracle-fusion.contract";
import {
  OracleEbsIntegrationSchema,
  OracleEbsIntegrationSettingsSchemaWithoutBase,
  oracleEbsContracts,
} from "./oracle-ebs.contract";
import {
  JiraIntegrationSchema,
  JiraIntegrationSettingsSchemaWithoutBase,
  jiraContracts,
} from "./jira.contract";
import {
  MicrosoftTeamsIntegrationSchema,
  MicrosoftTeamsIntegrationSettingsSchemaWithoutBase,
  microsoftTeamsContracts,
} from "./microsoft-teams.contract";
import {
  VoiceAgentIntegrationSchema,
  VoiceAgentIntegrationSettingsSchemaWithoutBase,
} from "./voice-agent";
import {
  SlackIntegrationSchema,
  SlackIntegrationSettingsSchemaWithoutBase,
} from "./slack.contract";
import { voiceAgentConfigContracts } from "./voice-agent-configs.contract";
import { voicePhoneNumberContracts } from "./voice-phone-numbers.contract";
import { vapiContracts } from "./vapi.contract";
import { famousContracts } from "./famous.contract";
import { erpSubmissionApiContracts } from "./erp-submission-apis.contract";

/**
 * Discriminated union of all integration types
 */
export const IntegrationSchema = z.discriminatedUnion("key", [
  FamousIntegrationSchema,
  PlaidIntegrationSchema,
  PaymentsIntegrationSchema,
  OracleFusionIntegrationSchema,
  OracleEbsIntegrationSchema,
  JiraIntegrationSchema,
  MicrosoftTeamsIntegrationSchema,
  VoiceAgentIntegrationSchema,
  SlackIntegrationSchema,
]);

/**
 * Integration DTO type
 */
export type IntegrationDTO = z.infer<typeof IntegrationSchema>;

/**
 * Update input schema for integrations
 */
export const IntegrationUpdateInputSchema = z.discriminatedUnion("key", [
  FamousIntegrationSettingsSchemaWithoutBase,
  PlaidIntegrationSettingsSchemaWithoutBase,
  PaymentsIntegrationSettingsSchemaWithoutBase,
  OracleFusionIntegrationSettingsSchemaWithoutBase,
  OracleEbsIntegrationSettingsSchemaWithoutBase,
  JiraIntegrationSettingsSchemaWithoutBase,
  MicrosoftTeamsIntegrationSettingsSchemaWithoutBase,
  VoiceAgentIntegrationSettingsSchemaWithoutBase,
  SlackIntegrationSettingsSchemaWithoutBase,
]);

// Contracts
const listIntegrations = oc
  .output(
    z.object({
      installed: z
        .partialRecord(z.enum(IntegrationKey), IntegrationSchema)
        .prefault({}),
      availability: z
        .partialRecord(z.enum(IntegrationKey), IntegrationAvailabilitySchema)
        .prefault({}),
    }),
  )
  .route({
    summary: "List installed integrations",
    description:
      "Returns the organization's installed integrations with secrets masked, plus whether each integration type is available on this server and why not.",
    method: "GET",
  });

const getIntegration = oc
  .input(z.object({ key: z.enum(IntegrationKey) }))
  .output(IntegrationSchema)
  .route({
    summary: "Get integration settings",
    description:
      "Returns one installed integration by type key with secrets masked; fails if the organization has not installed it.",
    method: "GET",
  });

const updateIntegration = oc
  .input(IntegrationUpdateInputSchema)
  .output(IntegrationSchema)
  .route({
    summary: "Save integration settings",
    description:
      "Creates or updates an integration's settings by type key. Passwords and tokens are encrypted; omitting one keeps the stored value. Payments accounts use separate actions.",
  });

export const integrationsContracts = {
  list: listIntegrations,
  get: getIntegration,
  update: updateIntegration,
  famous: famousContracts,
  plaid: plaidContracts,
  payments: paymentsIntegrationContracts,
  oracleFusion: oracleFusionContracts,
  oracleEbs: oracleEbsContracts,
  jira: jiraContracts,
  microsoftTeams: microsoftTeamsContracts,
  voiceAgentConfigs: voiceAgentConfigContracts,
  voicePhoneNumbers: voicePhoneNumberContracts,
  vapi: vapiContracts,
  erpSubmissionApis: erpSubmissionApiContracts,
} as const;

export type IntegrationsContracts = typeof integrationsContracts;

// Re-export base types and schemas
export {
  IntegrationKey,
  IntegrationBaseSchema,
  IntegrationAvailabilitySchema,
} from "./base";
export type { IntegrationAvailability } from "./base";

// Re-export Famous types and schemas
export {
  FamousIntegrationSettingsSchema,
  FamousIntegrationSchema,
} from "./famous";
export type { FamousIntegrationSettings, FamousIntegration } from "./famous";

// Re-export Plaid types, schemas, and contracts
export {
  PlaidIntegrationSettingsSchema,
  PlaidBankAccountSchema,
  PlaidIntegrationSchema,
  plaidContracts,
} from "./plaid.contract";
export type {
  PlaidIntegrationSettings,
  PlaidBankAccount,
  PlaidIntegration,
  PlaidContracts,
  CashReceiptFilterConfig,
} from "./plaid.contract";

// Re-export Payments types, schemas, and contracts
export {
  PaymentsIntegrationSettingsSchema,
  PaymentAccountSchema,
  PaymentsIntegrationSchema,
  CheckbookBankStatusSchema,
  BankAccountTypeSchema,
  CheckbookMarketplaceUserSchema,
  paymentsIntegrationContracts,
  isPaymentsIntegration,
  hasCheckbookCredentials,
  // KYB/KYC schemas for Checkbook onboarding
  USStateSchema,
  IndustryCodeSchema,
  CitizenshipStatusSchema,
  AddressSchema,
  BusinessInfoSchema,
  PrincipalOfficerInfoSchema,
  CheckbookOnboardingInfoSchema,
  // Display label arrays for form dropdowns
  US_STATES,
  INDUSTRY_CODES,
  CITIZENSHIP_OPTIONS,
} from "./payments.contract";

// Re-export Oracle Fusion types, schemas
export {
  OracleFusionIntegrationSettingsSchema,
  OracleFusionIntegrationSchema,
  FusionSyncStateSchema,
  oracleFusionContracts,
} from "./oracle-fusion.contract";
export type {
  OracleFusionIntegrationSettings,
  OracleFusionIntegration,
  OracleFusionContracts,
  FusionSyncStateDTO,
} from "./oracle-fusion.contract";

// Re-export Oracle EBS types, schemas
export {
  OracleEbsIntegrationSettingsSchema,
  OracleEbsIntegrationSchema,
  oracleEbsContracts,
} from "./oracle-ebs.contract";
export type {
  OracleEbsIntegrationSettings,
  OracleEbsIntegration,
  OracleEbsContracts,
} from "./oracle-ebs.contract";

// Re-export Jira types, schemas
export {
  JiraIntegrationSettingsSchema,
  JiraIntegrationSchema,
  jiraContracts,
} from "./jira.contract";
export type {
  JiraIntegrationSettings,
  JiraIntegration,
  JiraContracts,
} from "./jira.contract";

// Re-export Microsoft Teams types, schemas
export {
  MicrosoftTeamsIntegrationSettingsSchema,
  MicrosoftTeamsIntegrationSchema,
  microsoftTeamsContracts,
} from "./microsoft-teams.contract";
export type {
  MicrosoftTeamsIntegrationSettings,
  MicrosoftTeamsIntegration,
  MicrosoftTeamsContracts,
} from "./microsoft-teams.contract";

// Re-export Slack types, schemas
export {
  SlackIntegrationSettingsSchema,
  SlackIntegrationSchema,
} from "./slack.contract";
export type {
  SlackIntegrationSettings,
  SlackIntegration,
} from "./slack.contract";

export type {
  PaymentsIntegrationSettings,
  PaymentAccount,
  PaymentsIntegration,
  CheckbookBankStatus,
  BankAccountType,
  CheckbookMarketplaceUser,
  PaymentsIntegrationContracts,
  // KYB/KYC types for Checkbook onboarding
  USState,
  IndustryCode,
  CitizenshipStatus,
  Address,
  BusinessInfo,
  PrincipalOfficerInfo,
  CheckbookOnboardingInfo,
} from "./payments.contract";

// Re-export Voice Agent types and schemas
export {
  VoiceAgentIntegrationSettingsSchema,
  VoiceAgentIntegrationSchema,
  FirstMessageMode,
  BackgroundSound,
} from "./voice-agent";
export type {
  VoiceAgentIntegrationSettings,
  VoiceAgentIntegration,
} from "./voice-agent";

// Re-export Voice Agent Config types, schemas, and contracts
export {
  VoiceAgentAdvancedSettingsSchema,
  VoiceAgentConfigSchema,
  CreateVoiceAgentConfigInputSchema,
  UpdateVoiceAgentConfigInputSchema,
  ResourceOrigin,
  ResourceOriginSchema,
  TranscriberProvider,
  VoiceProvider,
  LanguagePreset,
  LanguagePresetSchema,
  LANGUAGE_PRESET_CONFIGS,
  TranscriberSettingsSchema,
  VoiceOverrideSettingsSchema,
  voiceAgentConfigContracts,
} from "./voice-agent-configs.contract";
export type {
  VoiceAgentAdvancedSettings,
  VoiceAgentConfigDTO,
  CreateVoiceAgentConfigInput,
  UpdateVoiceAgentConfigInput,
  VoiceAgentConfigContracts,
  LanguagePresetValue,
  TranscriberSettings,
  VoiceOverrideSettings,
} from "./voice-agent-configs.contract";

// Re-export Voice Phone Number types, schemas, and contracts
export {
  VoicePhoneNumberSchema,
  CreateVoicePhoneNumberInputSchema,
  UpdateVoicePhoneNumberInputSchema,
  PhoneNumberProvider,
  PhoneNumberProviderSchema,
  voicePhoneNumberContracts,
} from "./voice-phone-numbers.contract";
export type {
  VoicePhoneNumberDTO,
  CreateVoicePhoneNumberInput,
  UpdateVoicePhoneNumberInput,
  PhoneNumberProviderValue,
  VoicePhoneNumberContracts,
} from "./voice-phone-numbers.contract";

// Re-export Vapi proxy types, schemas, and contracts
export {
  VapiAssistantSummarySchema,
  VapiPhoneNumberSummarySchema,
  VapiVoiceSummarySchema,
  VapiAvailabilitySchema,
  CreateVapiAssistantInputSchema,
  CreateVapiPhoneNumberInputSchema,
  vapiContracts,
} from "./vapi.contract";
export type {
  VapiAssistantSummary,
  VapiPhoneNumberSummary,
  VapiVoiceSummary,
  VapiAvailability,
  CreateVapiAssistantInput,
  CreateVapiPhoneNumberInput,
  VapiContracts,
} from "./vapi.contract";

// Re-export Famous contracts and types
export { famousContracts } from "./famous.contract";
export type {
  FamousContracts,
  TranslationRefreshReport,
  FamousSqlPreviewColumn,
  FamousSqlQueryPreviewInput,
  FamousSqlQueryPreviewOutput,
  FamousConnectionStatus,
} from "./famous.contract";
export {
  ErpSubmissionApiSchema,
  type ErpSubmissionApi,
} from "./erp-submission-apis.contract";
